import ExportButton from "@components/ExportButton";
import { FilterBar, FilterRow } from "@components/FilterBar";
import ObjectSelect from "@components/ObjectSelect";
import PeriodFilter, { type PeriodRange } from "@components/PeriodFilter";
import { useObjects } from "@features/devices/api";
import { PageLayoutWithHelp } from "@features/page-help";
import { type ConsumptionRow, useConsumption } from "@features/reports/api";
import { buildExportUrl } from "@shared/buildExportUrl";
import { useGlobalObject } from "@shared/hooks/useGlobalObject";
import EnterpriseTable from "@shared/ui/enterprise-table";
import { useDefaultEmail } from "@shared/useDefaultEmail";
import { useMailComposer } from "@shared/useMailComposer";
import { useStandardPagination } from "@shared/useStandardPagination";
import { Alert, Checkbox, InputNumber, Radio, Select, Space, Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useCallback, useMemo, useState } from "react";

const measurementOptions = [
  { label: "A+", value: 1 },
  { label: "A-", value: 2 },
  { label: "R+", value: 3 },
  { label: "R-", value: 4 },
  { label: "A (1+2)", value: 12 },
  { label: "R (3+4)", value: 34 },
];

const roundModeOptions = [
  { label: "Математическое", value: "math" },
  { label: "Отсечение", value: "cut" },
];

const calcModeOptions = [
  { label: "По показаниям", value: "register" },
  { label: "По профилю", value: "profile" },
];

const formatFixed = (value: unknown, digits: number) => {
  return typeof value === "number" && Number.isFinite(value) ? value.toFixed(digits) : "—";
};

const renderIntervalsCell = (row: ConsumptionRow) => {
  const actual = row.intervals_actual ?? 0;
  const total = row.intervals_total ?? 0;
  if (actual === 0 && total === 0) {
    return "—";
  }
  const warn = total > 0 && actual < total;
  const content = `${actual}/${total}`;
  return <Typography.Text type={warn ? "danger" : undefined}>{content}</Typography.Text>;
};

const ReportsReadingsPage = () => {
  const objectsQuery = useObjects();
  const objects = objectsQuery.data ?? [];
  const { selectedObject, setSelectedObject } = useGlobalObject(objects);
  const [period, setPeriod] = useState<PeriodRange>(() => {
    const start = dayjs().startOf("month");
    return { from: start, to: start.add(1, "month") };
  });
  const [measurement, setMeasurement] = useState<number>(1);
  const [calcMode, setCalcMode] = useState<string>("register");
  const [offsetMinutes, setOffsetMinutes] = useState<number>(0);
  const [roundDigits, setRoundDigits] = useState<number>(3);
  const [roundMode, setRoundMode] = useState<string>("math");
  const [useBypass, setUseBypass] = useState<boolean>(false);

  const regionID = selectedObject?.regionId;
  const objectID = selectedObject?.objectId;

  const fromIso = period.from?.format("YYYY-MM-DD");
  const toIso = period.to?.format("YYYY-MM-DD");

  const consumptionQuery = useConsumption({
    region: regionID,
    object: objectID,
    from: fromIso,
    to: toIso,
    measurement,
    calc: calcMode,
    offsetMinutes,
    roundDigits,
    roundMode,
    useBypass,
  });

  const data = consumptionQuery.data ?? [];
  const pagination = useStandardPagination(data.length);

  const totals = useMemo(
    () =>
      data.reduce(
        (acc, row) => {
          if (row.energy != null) {
            acc.energy += row.energy;
          }
          if (row.losses != null) {
            acc.losses += row.losses;
          }
          return acc;
        },
        { energy: 0, losses: 0 },
      ),
    [data],
  );

  const columns: ColumnsType<ConsumptionRow> = [
    { title: "Фидер", dataIndex: "feeder_name", width: 200, ellipsis: true },
    { title: "Тип", dataIndex: "measurement_label", width: 80 },
    { title: "Счётчик №", dataIndex: "channel_number", width: 120 },
    {
      title: "Ct × Vt × M",
      dataIndex: "transformer_ratio",
      width: 130,
      render: (value?: number) => formatFixed(value, 3),
    },
    {
      title: "% потерь",
      dataIndex: "loss_percent",
      width: 110,
      render: (value?: number) => formatFixed(value, 2),
    },
    {
      title: "Начало",
      dataIndex: "register_start",
      width: 140,
      render: (value?: number) => formatFixed(value, roundDigits),
    },
    {
      title: "Конец",
      dataIndex: "register_end",
      width: 140,
      render: (value?: number) => formatFixed(value, roundDigits),
    },
    {
      title: "∆ показаний",
      dataIndex: "register_delta",
      width: 140,
      render: (value?: number) => formatFixed(value, roundDigits),
    },
    {
      title: "Потери, кВт⋅ч",
      dataIndex: "losses",
      width: 140,
      render: (value?: number) => formatFixed(value, 4),
    },
    {
      title: "Энергия, кВт⋅ч",
      dataIndex: "energy",
      width: 160,
      render: (value?: number) => formatFixed(value, 4),
    },
    {
      title: "Статус",
      dataIndex: "status",
      width: 100,
      render: (value?: number) =>
        value !== undefined ? <Tag color={value === 0 ? "green" : "red"}>{value}</Tag> : "—",
    },
    {
      title: "Интервалы",
      dataIndex: "intervals_actual",
      width: 130,
      render: (_value, row) => renderIntervalsCell(row),
    },
    {
      title: "События",
      dataIndex: "events",
      width: 110,
      render: (value?: number) => (value ? value : 0),
    },
  ];

  const ready = regionID !== undefined && objectID !== undefined && fromIso && toIso;
  const exportUrl = ready
    ? buildExportUrl("/api/reports/consumption", {
        region: regionID,
        object: objectID,
        from: fromIso,
        to: toIso,
        measurement,
        calc: calcMode,
        offset: offsetMinutes,
        round_digits: roundDigits,
        round_mode: roundMode,
        use_bypass: useBypass || undefined,
      })
    : undefined;
  const currentObject = useMemo(
    () => objects.find((obj) => obj.n_ob === objectID && obj.syb_rnk === regionID),
    [objects, objectID, regionID],
  );
  const defaultEmail = useDefaultEmail();
  const { openComposer, mailModal, sendDisabled } = useMailComposer({ defaultEmail, exportUrl });

  const measurementLabel =
    measurementOptions.find((option) => option.value === measurement)?.label ?? `№${measurement}`;
  const calcLabel = calcModeOptions.find((option) => option.value === calcMode)?.label ?? calcMode;
  const formatDate = (value?: dayjs.Dayjs) => (value ? value.format("DD.MM.YYYY HH:mm") : "—");
  const periodLabel = `${formatDate(period.from)} — ${formatDate(period.to)}`;

  const handleSendMail = useCallback(() => {
    if (!ready) {
      return;
    }
    const objectLabel = currentObject?.txt
      ? `${currentObject.txt} (№${currentObject.n_ob})`
      : objectID
        ? `Объект №${objectID}`
        : "Объект";
    openComposer({
      subject: `Показания — ${objectLabel}`,
      body: `Отчёт: Показания по счётчикам\nОбъект: ${objectLabel}\nПериод: ${periodLabel}\nПоказатель: ${measurementLabel}\nМетод расчёта: ${calcLabel}\n\nExcel-файл во вложении.`,
      filename: `consumption_${regionID ?? "r"}_${objectID ?? "obj"}_${period.from?.format("YYYYMMDD") ?? "period"}.xlsx`,
    });
  }, [
    ready,
    currentObject,
    objectID,
    openComposer,
    regionID,
    periodLabel,
    measurementLabel,
    calcLabel,
    period.from,
  ]);

  return (
    <PageLayoutWithHelp
      title="Показания по счётчикам объекта"
      extra={<ExportButton url={exportUrl} onSend={handleSendMail} sendDisabled={sendDisabled} />}
    >
      <Space orientation="vertical" size="large" style={{ width: "100%" }}>
        <FilterBar>
          <FilterRow>
            <ObjectSelect
              objects={objects}
              loading={objectsQuery.isLoading}
              value={selectedObject}
              onChange={(value) => setSelectedObject(value)}
              style={{ minWidth: 320, flex: 1 }}
            />
            <Select
              style={{ width: 140 }}
              options={measurementOptions}
              value={measurement}
              onChange={(value) => setMeasurement(value)}
            />
            <Radio.Group
              options={calcModeOptions}
              value={calcMode}
              onChange={(e) => setCalcMode(e.target.value)}
              optionType="button"
              buttonStyle="solid"
            />
          </FilterRow>
          <FilterRow>
            <Select
              style={{ width: 140 }}
              value={offsetMinutes}
              onChange={(value) => setOffsetMinutes(value)}
              options={[
                { label: "0 мин", value: 0 },
                { label: "30 мин", value: 30 },
                { label: "60 мин", value: 60 },
              ]}
            />
            <Space.Compact>
              <Typography.Text
                style={{
                  padding: "4px 8px",
                  border: "1px solid #d9d9d9",
                  borderRight: 0,
                  borderRadius: 6,
                }}
              >
                Знак.
              </Typography.Text>
              <InputNumber
                min={0}
                max={6}
                value={roundDigits}
                onChange={(value) => setRoundDigits(value ?? 0)}
                style={{ width: 110 }}
              />
            </Space.Compact>
            <Select
              options={roundModeOptions}
              value={roundMode}
              onChange={(value) => setRoundMode(value)}
              style={{ width: 200 }}
            />
            <Checkbox checked={useBypass} onChange={(e) => setUseBypass(e.target.checked)}>
              Учитывать обходные
            </Checkbox>
          </FilterRow>
          <div style={{ width: "100%" }}>
            <PeriodFilter value={period} onChange={setPeriod} />
          </div>
        </FilterBar>
        {!ready && (
          <Alert type="info" showIcon title="Выберите объект и период" style={{ maxWidth: 500 }} />
        )}
        {ready && consumptionQuery.isError && (
          <Alert
            type="error"
            showIcon
            message="Ошибка загрузки данных"
            description="Не удалось загрузить показания. Попробуйте обновить страницу или выбрать другой период."
          />
        )}
        {ready && (
          <EnterpriseTable<ConsumptionRow>
            rowKey={(row) => `${row.feeder_id}-${row.measurement_type}`}
            columns={columns}
            dataSource={data}
            loading={consumptionQuery.isLoading}
            scroll={{ x: 1200 }}
            pagination={pagination}
            size="small"
            toolbar={{ reload: true, onReload: () => consumptionQuery.refetch() }}
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={8}>
                    <Typography.Text strong>Всего:</Typography.Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={8}>
                    <Typography.Text strong>{totals.losses.toFixed(4)}</Typography.Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={9}>
                    <Typography.Text strong>{totals.energy.toFixed(4)}</Typography.Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={10} colSpan={3}>
                    &nbsp;
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
        )}
        {mailModal}
      </Space>
    </PageLayoutWithHelp>
  );
};

export default ReportsReadingsPage;
